/**
 * 協調隊列重抓的單一 timer，收斂三種來源（snapshot 續接、queue_updated 廣播、失敗退避）
 * 對同一顆 timer 的競爭寫入，並以世代編號讓廣播判定要重抓後，先於廣播送出的在途請求
 * 回應失效。副作用（setTimeout/clearTimeout/Date.now/實際發請求）全由呼叫端透過 deps 注入，
 * 這裡只管狀態機。
 *
 * 內部不變式：
 * - 世代只在 fromBroadcast() 裡 +1，而 fromBroadcast() 必定排定一次重抓。這條是「丟棄舊回應」
 *   安全的唯一依據——沒有它，被丟掉的回應會變成沒有下一次補救。任何新增的 bump 點都必須同時
 *   保證排定重抓。
 * - timer 觸發時（run() 之前）清掉待處理廣播、不動世代。觸發不是作廢事件；世代要留到那趟新
 *   請求的回應回來時仍然相等。
 * - 待處理廣播存的是絕對到期時刻（now() + delayMs），不是相對延遲。期間任何一次
 *   re-arm（fromSnapshot 取較早值）都要用「目標時刻 − 當下 now()」重算剩餘延遲；若存相對值，
 *   每次 re-arm 都會把目標時刻從呼叫當下重新起算一次，廣播排定的重抓會被沿路的每一次
 *   在途回應往後推，而非釘死在原本的絕對時刻。
 * - now 注入的是未校正的 Date.now，不是對時後的 clock.now()。這顆 timer 算的是本地相對延遲，
 *   混進伺服器時鐘偏移會讓「目標時刻 − now()」整段偏掉。
 *
 * @param {{
 *   schedule: function(function(): void, number): *,
 *   cancel: function(*): void,
 *   now: function(): number,
 *   run: function(): void
 * }} deps
 * @returns {{
 *   fromSnapshot: function(number|null): void,
 *   fromBroadcast: function(number): void,
 *   fromRetry: function(number): void,
 *   cancelAll: function(): void,
 *   currentGeneration: function(): number,
 *   isStale: function(number): boolean
 * }}
 */
export function createQueueRefetchCoordinator({ schedule, cancel, now, run }) {
    let timerHandle = null;
    let broadcastDeadlineAt = null;
    let generation = 0;

    function clearTimer() {
        if (timerHandle !== null) {
            cancel(timerHandle);
            timerHandle = null;
        }
    }

    /** 把「絕對到期時刻」轉成此刻該排的相對延遲後排定；不論排幾次都指向同一個目標時刻。 */
    function armAtDeadline(deadlineAtMs) {
        clearTimer();
        timerHandle = schedule(function() {
            timerHandle = null;
            broadcastDeadlineAt = null;
            run();
        }, Math.max(0, deadlineAtMs - now()));
    }

    function fromSnapshot(delayMs) {
        if (broadcastDeadlineAt !== null) {
            var snapshotDeadlineAt = delayMs === null ? null : now() + delayMs;
            armAtDeadline(snapshotDeadlineAt === null ? broadcastDeadlineAt : Math.min(snapshotDeadlineAt, broadcastDeadlineAt));
            return;
        }
        if (delayMs === null) {
            clearTimer();
            return;
        }
        armAtDeadline(now() + delayMs);
    }

    function fromBroadcast(delayMs) {
        generation++;
        var deadlineAt = now() + delayMs;
        broadcastDeadlineAt = broadcastDeadlineAt === null ? deadlineAt : Math.min(deadlineAt, broadcastDeadlineAt);
        armAtDeadline(broadcastDeadlineAt);
    }

    // 退避期間在途回應照樣作廢（isStale 由世代把關，這裡不還原世代），補救靠這次重試本身。
    function fromRetry(delayMs) {
        broadcastDeadlineAt = null;
        armAtDeadline(now() + delayMs);
    }

    function cancelAll() {
        broadcastDeadlineAt = null;
        clearTimer();
    }

    function currentGeneration() {
        return generation;
    }

    function isStale(generationAtSend) {
        return generationAtSend !== generation;
    }

    return { fromSnapshot, fromBroadcast, fromRetry, cancelAll, currentGeneration, isStale };
}
